import { useState, useRef, useCallback } from 'react';
import type { EditorPage } from '@/mocks/editorLayout';

interface PageManagerProps {
  pages: EditorPage[];
  currentPageId: string;
  onPageSelect: (pageId: string) => void;
  onReorderPages: (newPages: EditorPage[]) => void;
  onAddPage: (section: string, type: EditorPage['type']) => void;
  onDeletePage: (pageId: string) => void;
  onDuplicatePage: (pageId: string) => void;
  onChangePageStatus: (pageId: string, status: EditorPage['status']) => void;
}

const SECTIONS = [
  { label: 'Capa', value: 'Capa', type: 'capa' as const, icon: 'ri-newspaper-line', color: 'text-[#C41230]' },
  { label: 'Política', value: 'Política', type: 'interna' as const, icon: 'ri-government-line', color: 'text-[#1A3A5C]' },
  { label: 'Economia', value: 'Economia', type: 'interna' as const, icon: 'ri-line-chart-line', color: 'text-[#1A6B3C]' },
  { label: 'Cultura', value: 'Cultura', type: 'interna' as const, icon: 'ri-palette-line', color: 'text-[#7B3A9E]' },
  { label: 'Esportes', value: 'Esportes', type: 'interna' as const, icon: 'ri-football-line', color: 'text-[#D45B00]' },
  { label: 'Cidade', value: 'Cidade', type: 'interna' as const, icon: 'ri-building-line', color: 'text-[#444444]' },
  { label: 'Ciência', value: 'Ciência', type: 'interna' as const, icon: 'ri-microscope-line', color: 'text-[#0A6E7A]' },
  { label: 'Opinião', value: 'Opinião', type: 'interna' as const, icon: 'ri-chat-quote-line', color: 'text-[#8A6A2A]' },
  { label: 'Internacional', value: 'Internacional', type: 'interna' as const, icon: 'ri-global-line', color: 'text-[#3A3A9E]' },
  { label: 'Saúde', value: 'Saúde', type: 'interna' as const, icon: 'ri-heart-pulse-line', color: 'text-[#C41230]' },
  { label: 'Contracapa', value: 'Contracapa', type: 'contracapa' as const, icon: 'ri-pages-line', color: 'text-[#555555]' },
];

const statusColors: Record<string, string> = {
  aprovado: 'bg-green-500',
  revisão: 'bg-yellow-400',
  rascunho: 'bg-[#CCCCCC]',
};

const statusLabels: Record<string, string> = {
  aprovado: 'Aprovado',
  revisão: 'Em Revisão',
  rascunho: 'Rascunho',
};

export default function PageManager({
  pages,
  currentPageId,
  onPageSelect,
  onReorderPages,
  onAddPage,
  onDeletePage,
  onDuplicatePage,
  onChangePageStatus,
}: PageManagerProps) {
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState<typeof SECTIONS[0] | null>(null);
  const [contextMenu, setContextMenu] = useState<{ pageId: string; x: number; y: number } | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleDragStart = (index: number, pageId: string) => {
    dragItem.current = index;
    setDraggedId(pageId);
  };

  const handleDragEnter = (index: number, pageId: string) => {
    dragOverItem.current = index;
    setDragOverId(pageId);
  };

  const handleDragEnd = useCallback(() => {
    if (dragItem.current === null || dragOverItem.current === null) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    if (dragItem.current === dragOverItem.current) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const reordered = [...pages];
    const draggedPage = reordered.splice(dragItem.current, 1)[0];
    reordered.splice(dragOverItem.current, 0, draggedPage);
    const renumbered = reordered.map((p, i) => ({ ...p, number: i + 1 }));
    onReorderPages(renumbered);
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggedId(null);
    setDragOverId(null);
  }, [pages, onReorderPages]);

  const handleContextMenu = (e: React.MouseEvent, pageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ pageId, x: e.clientX, y: e.clientY });
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleAddPage = () => {
    if (!selectedSection) return;
    onAddPage(selectedSection.value, selectedSection.type);
    setShowNewPageModal(false);
    setSelectedSection(null);
  };

  return (
    <div className="p-3 relative" onClick={closeContextMenu}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-bold text-[#111111] font-sans uppercase tracking-wide">Páginas</p>
          <p className="text-[9px] text-[#999999] font-sans">{pages.length} pág. · Arraste para reordenar</p>
        </div>
        <button
          onClick={() => setShowNewPageModal(true)}
          title="Nova Página"
          className="flex items-center gap-1 px-2 py-1 bg-[#C41230] text-white cursor-pointer hover:bg-[#A00E25] transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-xs"></i>
          <span className="text-[9px] font-bold font-sans uppercase tracking-wide">Nova</span>
        </button>
      </div>

      {/* Pages List */}
      <div className="space-y-2">
        {pages.map((page, index) => (
          <div
            key={page.id}
            draggable
            onDragStart={() => handleDragStart(index, page.id)}
            onDragEnter={() => handleDragEnter(index, page.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onContextMenu={(e) => handleContextMenu(e, page.id)}
            className={`relative cursor-pointer transition-all select-none ${
              draggedId === page.id ? 'opacity-40 scale-95' : ''
            } ${dragOverId === page.id && draggedId !== page.id ? 'ring-2 ring-[#C41230] ring-offset-1' : ''}`}
          >
            <div
              onClick={() => onPageSelect(page.id)}
              className={`border transition-all ${
                currentPageId === page.id
                  ? 'border-[#C41230] ring-2 ring-[#C41230]/30'
                  : 'border-[#E0E0E0] hover:border-[#AAAAAA]'
              }`}
            >
              {/* Page Thumbnail */}
              <div className="relative w-full aspect-[3/4] bg-[#F8F8F8] overflow-hidden">
                {/* Mini newspaper preview */}
                <div className="absolute inset-0 p-1.5 flex flex-col gap-0.5">
                  <div className="h-2 bg-[#111111] w-full flex-shrink-0"></div>
                  <div className="flex gap-0.5 flex-1">
                    <div className="flex-1 flex flex-col gap-0.5">
                      {page.blocks.slice(0, 3).map((b) => (
                        <div
                          key={b.id}
                          className={`flex-1 ${
                            b.type === 'image' ? 'bg-[#D0D0D0]' :
                            b.type === 'ad' ? 'bg-[#E8E8E8] border border-dashed border-[#CCCCCC]' :
                            b.type === 'title' ? 'bg-[#111111]' :
                            'bg-[#F0F0F0]'
                          }`}
                        ></div>
                      ))}
                      {page.blocks.length === 0 && (
                        <div className="flex-1 flex items-center justify-center">
                          <i className="ri-add-line text-[#CCCCCC] text-lg"></i>
                        </div>
                      )}
                    </div>
                    {page.blocks.length > 2 && (
                      <div className="w-1/3 flex flex-col gap-0.5">
                        <div className="flex-1 bg-[#E0E0E0]"></div>
                        <div className="h-1/3 bg-[#E8E8E8]"></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Dot */}
                <div className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white ${statusColors[page.status]}`}></div>

                {/* Page number badge */}
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[8px] font-bold font-sans px-1 py-0.5 leading-none">
                  {page.number}
                </div>

                {/* Drag handle */}
                <div className="absolute top-1 left-1 text-[#999999] opacity-0 group-hover:opacity-100">
                  <i className="ri-drag-move-2-line text-xs"></i>
                </div>

                {/* Drag indicator overlay */}
                {draggedId !== page.id && (
                  <div className="absolute inset-0 bg-transparent group-hover:bg-black/5 transition-colors flex items-center justify-center pointer-events-none">
                    <i className="ri-drag-move-2-line text-black/20 text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </div>
                )}
              </div>

              {/* Page Info */}
              <div className="px-1.5 py-1.5 bg-white">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-[#111111] font-sans truncate">{page.section}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleContextMenu(e, page.id); }}
                    className="w-4 h-4 flex items-center justify-center text-[#BBBBBB] hover:text-[#111111] cursor-pointer ml-1 flex-shrink-0"
                  >
                    <i className="ri-more-2-fill text-xs"></i>
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${statusColors[page.status]}`}></div>
                  <p className="text-[8px] text-[#999999] font-sans">{statusLabels[page.status]}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white border border-[#E0E0E0] shadow-lg py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-[9px] font-bold text-[#999999] font-sans uppercase tracking-wide px-3 py-1.5 border-b border-[#F0F0F0]">Ações da Página</p>
          <button
            onClick={() => { onDuplicatePage(contextMenu.pageId); closeContextMenu(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-sans text-[#111111] hover:bg-[#F8F8F8] cursor-pointer transition-colors"
          >
            <i className="ri-file-copy-line text-[#666666] text-sm"></i>
            Duplicar página
          </button>
          <div className="border-t border-[#F0F0F0] my-1">
            <p className="text-[9px] font-bold text-[#999999] font-sans uppercase tracking-wide px-3 py-1">Status</p>
            {(['rascunho', 'revisão', 'aprovado'] as const).map((s) => (
              <button
                key={s}
                onClick={() => { onChangePageStatus(contextMenu.pageId, s); closeContextMenu(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-sans text-[#111111] hover:bg-[#F8F8F8] cursor-pointer transition-colors"
              >
                <div className={`w-2 h-2 rounded-full ${statusColors[s]}`}></div>
                {statusLabels[s]}
              </button>
            ))}
          </div>
          <div className="border-t border-[#F0F0F0] my-1">
            <button
              onClick={() => { onDeletePage(contextMenu.pageId); closeContextMenu(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-sans text-red-600 hover:bg-red-50 cursor-pointer transition-colors"
            >
              <i className="ri-delete-bin-line text-sm"></i>
              Excluir página
            </button>
          </div>
        </div>
      )}

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { setShowNewPageModal(false); setSelectedSection(null); }}>
          <div className="bg-white w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-[#111111]">
              <div className="w-7 h-7 flex items-center justify-center bg-[#C41230]">
                <i className="ri-add-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#111111] font-serif">Nova Página</p>
                <p className="text-[10px] text-[#999999] font-sans">Escolha a editoria da nova página</p>
              </div>
              <button onClick={() => { setShowNewPageModal(false); setSelectedSection(null); }} className="ml-auto w-7 h-7 flex items-center justify-center text-[#999999] hover:text-[#111111] cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Sections Grid */}
            <div className="p-4">
              <p className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide mb-3">Selecione a editoria</p>
              <div className="grid grid-cols-3 gap-2">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.value}
                    onClick={() => setSelectedSection(sec)}
                    className={`flex flex-col items-center gap-1.5 p-3 border-2 cursor-pointer transition-all ${
                      selectedSection?.value === sec.value
                        ? 'border-[#C41230] bg-[#FFF5F7]'
                        : 'border-[#E0E0E0] hover:border-[#AAAAAA] hover:bg-[#F8F8F8]'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center`}>
                      <i className={`${sec.icon} text-xl ${selectedSection?.value === sec.value ? 'text-[#C41230]' : sec.color}`}></i>
                    </div>
                    <p className={`text-[9px] font-bold font-sans uppercase tracking-wide text-center leading-tight ${
                      selectedSection?.value === sec.value ? 'text-[#C41230]' : 'text-[#444444]'
                    }`}>{sec.label}</p>
                    {sec.type !== 'interna' && (
                      <span className="text-[7px] font-sans text-white bg-[#111111] px-1 py-0.5 uppercase tracking-wide leading-none">{sec.type}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Info */}
            {selectedSection && (
              <div className="mx-4 mb-3 p-3 bg-[#F8F8F8] border border-[#E0E0E0] flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center bg-white border border-[#E0E0E0]`}>
                  <i className={`${selectedSection.icon} text-lg ${selectedSection.color}`}></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-[#111111] font-sans">{selectedSection.label}</p>
                  <p className="text-[10px] text-[#999999] font-sans capitalize">
                    Tipo: {selectedSection.type} · Pág. {pages.length + 1} · Status: rascunho
                  </p>
                </div>
                <i className="ri-check-line text-[#C41230] text-lg ml-auto"></i>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 px-4 pb-4">
              <button
                onClick={() => { setShowNewPageModal(false); setSelectedSection(null); }}
                className="flex-1 py-2.5 border border-[#E0E0E0] text-xs font-sans text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddPage}
                disabled={!selectedSection}
                className={`flex-1 py-2.5 text-xs font-sans font-bold transition-colors ${
                  selectedSection
                    ? 'bg-[#111111] text-white hover:bg-[#333333] cursor-pointer'
                    : 'bg-[#E0E0E0] text-[#999999] cursor-not-allowed'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-add-line"></i>
                  Criar Página
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
